import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";

/**
 * Format Unix timestamp to readable date-time string
 */
const formatUTCTimeWithSec = (unixTime) => {
  return moment(unixTime).format("YYYY-MM-DD HH:mm:ss");
};

/**
 * Converts production data to PDF with complete data on each row
 * Each device gets its full parent context (production + item data)
 * Matches the CSV structure exactly
 */
export const generateProductionPDF = (responseData) => {
  // Validate input
  if (!responseData) {
    throw new Error("Production data is null or undefined");
  }

  const dataArray = responseData.data || responseData;

  if (!Array.isArray(dataArray)) {
    throw new Error("Expected data to be an array");
  }

  // Create PDF document
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  // Add title
  doc.setFontSize(16);
  doc.setFont(undefined, "bold");
  doc.text("Production Report", 14, 15);

  // Add generation date
  doc.setFontSize(10);
  doc.setFont(undefined, "normal");
  doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);

  // Prepare table data - each row has complete data
  const tableData = [];

  dataArray.forEach((production) => {
    if (
      !production.Production_export_items ||
      production.Production_export_items.length === 0
    ) {
      // Production with no items
      tableData.push([
        production.ProductionNo || "",
        production.ProductionName || "",
        production.ProductionDesc || "",
        production.ProductionTime
          ? formatUTCTimeWithSec(production.ProductionTime)
          : "",
        "No items",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
      ]);
      return;
    }

    production.Production_export_items.forEach((item) => {
      if (
        !item.Production_item_devices ||
        item.Production_item_devices.length === 0
      ) {
        // Item with no devices - show production and item data
        tableData.push([
          production.ProductionNo || "",
          production.ProductionName || "",
          production.ProductionDesc || "",
          production.ProductionTime
            ? formatUTCTimeWithSec(production.ProductionTime)
            : "",
          item.ProductionItemNo || "",
          item.Product || "",
          item.Hardware || "",
          "No devices",
          "",
          "",
          "",
          "",
        ]);
        return;
      }

      // For each device, include full production and item context
      item.Production_item_devices.forEach((device) => {
        tableData.push([
          // Production info (repeated for every device)
          production.ProductionNo || "",
          production.ProductionName || "",
          production.ProductionDesc || "",
          production.ProductionTime
            ? formatUTCTimeWithSec(production.ProductionTime)
            : "",
          // Item info (repeated for every device in the item)
          item.ProductionItemNo || "",
          item.Product || "",
          item.Hardware || "",
          // Device info (unique for each device)
          device.DeviceNo || "",
          device.Mac || "",
          device.NodeType || "",
          device.Type || "",
          device.Devices || "",
        ]);
      });
    });
  });

  // Generate table using autoTable
  autoTable(doc, {
    startY: 28,
    head: [
      [
        {
          content: "Production Info",
          colSpan: 4,
          styles: {
            fillColor: [68, 114, 196],
            halign: "center",
            fontStyle: "bold",
            textColor: [255, 255, 255],
          },
        },
        {
          content: "Production Item Details",
          colSpan: 3,
          styles: {
            fillColor: [68, 114, 196],
            halign: "center",
            fontStyle: "bold",
            textColor: [255, 255, 255],
          },
        },
        {
          content: "Device Details",
          colSpan: 5,
          styles: {
            fillColor: [68, 114, 196],
            halign: "center",
            fontStyle: "bold",
            textColor: [255, 255, 255],
          },
        },
      ],
      [
        "No",
        "Name",
        "Description",
        "Production Time",
        "Item No",
        "Product",
        "Hardware",
        "Device No",
        "MAC",
        "Node Type",
        "Door",
        "Devices",
      ],
    ],
    body: tableData,
    styles: {
      fontSize: 7,
      cellPadding: 2,
      lineColor: [200, 200, 200],
      lineWidth: 0.1,
      valign: "top",
    },
    headStyles: {
      fillColor: [217, 225, 242],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      fontSize: 8,
      halign: "center",
    },
    columnStyles: {
      0: { cellWidth: 15, fillColor: [226, 239, 218] }, // Production No - light green
      1: { cellWidth: 25, fillColor: [226, 239, 218] }, // Production Name
      2: { cellWidth: 30, fillColor: [226, 239, 218] }, // Production Desc
      3: { cellWidth: 25, fillColor: [226, 239, 218] }, // Production Time
      4: { cellWidth: 15, fillColor: [233, 215, 248] }, // Item No - light purple
      5: { cellWidth: 25, fillColor: [233, 215, 248] }, // Product - light purple
      6: { cellWidth: 25, fillColor: [233, 215, 248] }, // Hardware - light purple
      7: { cellWidth: 15, fillColor: [207, 226, 243] }, // Device No - light blue
      8: { cellWidth: 25, fillColor: [207, 226, 243] }, // MAC
      9: { cellWidth: 20, fillColor: [207, 226, 243] }, // Node Type
      10: { cellWidth: 15, fillColor: [207, 226, 243] }, // Type
      11: { cellWidth: 25, fillColor: [207, 226, 243] }, // Devices
    },
    margin: { top: 28, left: 8, right: 8 },
  });

  return doc;
};

/**
 * Download PDF file
 */
export const downloadProductionPDF = (
  productionData,
  filename = "production_report.pdf",
) => {
  try {
    const doc = generateProductionPDF(productionData);
    doc.save(filename);
    return true;
  } catch (error) {
    console.error("Error creating PDF:", error);
    throw error;
  }
};
